import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import ApibooLoader from '../ui/ApibooLoader';
import Tooltip from '../ui/Tooltip';
import { useChatSearchStore } from '../../store/chatSearchStore';
import { loadMoreChatSearch, resortChatSearch, cancelChatSearch } from '../../hooks/useChatSearch';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import ChatSearchResultCard from './ChatSearchResultCard';
import './ChatSearch.css';

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function groupByDay(results) {
  const groups = [];
  for (const m of results) {
    const d = m.inserted_at ? new Date(m.inserted_at) : null;
    const key = d && !Number.isNaN(d.getTime()) ? dayKey(d) : 'unknown';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(m);
    else groups.push({ key, date: d, items: [m] });
  }
  return groups;
}

/** The result count line: exact when the server counted, "N+" while more may exist. */
export function countLabel(t, { total, totalCapped, results, complete }) {
  if (total != null) {
    if (totalCapped) return t('chat.search.countMore', { count: total });
    if (total === 0) return t('chat.search.countNone');
    if (total === 1) return t('chat.search.countOne');
    return t('chat.search.count', { count: total });
  }
  const n = results.length;
  if (!complete) return t('chat.search.countMore', { count: n });
  if (n === 0) return t('chat.search.countNone');
  if (n === 1) return t('chat.search.countOne');
  return t('chat.search.count', { count: n });
}

/**
 * The results panel beside the conversation (D11): count, sort, day-grouped
 * result cards, and "Search older messages" while the server has more.
 */
export default function ChatSearchResults({ channel }) {
  const { t } = useTranslation();
  const channelId = channel?.id ?? null;
  const results = useChatSearchStore((s) => s.results);
  const status = useChatSearchStore((s) => s.status);
  const error = useChatSearchStore((s) => s.error);
  const sort = useChatSearchStore((s) => s.sort);
  const complete = useChatSearchStore((s) => s.complete);
  const nextCursor = useChatSearchStore((s) => s.nextCursor);
  const total = useChatSearchStore((s) => s.total);
  const totalCapped = useChatSearchStore((s) => s.totalCapped);
  const historyDays = useChatSearchStore((s) => s.historyDays);
  const terms = useChatSearchStore((s) => s.terms);
  const ran = useChatSearchStore((s) => s.ran);

  const loading = status === 'loading';
  const close = () => {
    cancelChatSearch();
    useChatSearchStore.getState().close();
  };

  // The first page loads with the app's panel loader in the body (the same
  // ApibooLoader EditorPane shows), not a "Searching…" headline; screen
  // readers still hear it through the status line.
  const firstLoad = loading && !results.length && !error;
  let headline = null;
  if (error || firstLoad) headline = null;
  else if (ran) headline = countLabel(t, { total, totalCapped, results, complete });

  return (
    <aside className="chat-search-panel" aria-label={t('chat.search.panelLabel')}>
      <div className="chat-search-panel-head">
        <span className="chat-search-panel-count" role="status">
          {headline}
          {firstLoad && <span className="chat-search-sr">{t('chat.search.searching')}</span>}
        </span>
        <div className="chat-search-sort" role="group" aria-label={t('chat.search.sortLabel')}>
          {['newest', 'oldest'].map((v) => (
            <button
              key={v}
              type="button"
              className={`chat-search-sort-btn${sort === v ? ' chat-search-sort-btn--on' : ''}`}
              aria-pressed={sort === v}
              onClick={() => resortChatSearch(v)}
            >
              {t(v === 'newest' ? 'chat.search.sortNewest' : 'chat.search.sortOldest')}
            </button>
          ))}
        </div>
        <Tooltip content={t('chat.search.close')} side="bottom">
          <button type="button" className="chat-icon-btn" onClick={close}>
            <X size={15} />
          </button>
        </Tooltip>
      </div>

      {historyDays != null && (
        <div className="chat-search-window-note">
          {t('chat.search.windowNote', { days: historyDays })}{' '}
          <button
            type="button"
            className="chat-search-link-btn"
            onClick={() => {
              setUpgradeReason(t('chat.search.windowNote', { days: historyDays }));
              setUpgradeModalOpen(true);
            }}
          >
            {t('chat.search.upgrade')}
          </button>
        </div>
      )}

      {error && (
        <div className="chat-search-error" role="alert">
          {t(error.key, { value: error.value })}
        </div>
      )}

      {firstLoad ? (
        <div className="apiboo-panel-loader chat-search-loader" data-testid="chat-search-loader">
          <ApibooLoader size={72} />
        </div>
      ) : (
        <OsScroll className="chat-search-list">
          <div className="chat-search-list-inner">
            {groupByDay(results).map((g) => (
              <section key={g.key} className="chat-search-day">
                <div className="chat-search-day-label">
                  {g.date ? g.date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                </div>
                {g.items.map((m) => (
                  <ChatSearchResultCard key={m.id ?? m.server_seq} message={m} channelId={channelId} terms={terms} />
                ))}
              </section>
            ))}
            <div className="chat-search-foot">
              {loading && results.length > 0 && (
                <span className="chat-search-muted">
                  <Loader2 size={12} className="chat-search-spin" /> {t('chat.search.searching')}
                </span>
              )}
              {!loading && !error && !complete && nextCursor != null && (
                <button type="button" className="chat-search-more-btn" onClick={() => loadMoreChatSearch()}>
                  {t('chat.search.keepSearching')}
                </button>
              )}
              {!loading && complete && results.length > 0 && <span className="chat-search-muted">{t('chat.search.end')}</span>}
            </div>
          </div>
        </OsScroll>
      )}
    </aside>
  );
}
